import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { PageHeader } from '@/components/PageHeader';
import { Database, RefreshCw, Trash2, CheckCircle, AlertTriangle, Info } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

export default function DeveloperTools() {
  const { toast } = useToast();
  const [isSeeding, setIsSeeding] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [seedResults, setSeedResults] = useState<any>(null);

  const handleSeedData = async () => {
    setIsSeeding(true);
    setProgress(0);
    setSeedResults(null);

    try {
      // Check session first
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        throw new Error('No active session. Please log in again.');
      }

      setProgress(20);

      const { data, error } = await supabase.functions.invoke('seed-dummy-data', {
        body: { action: 'seed_all' }
      });

      if (error) throw error;

      setSeedResults(data);
      setProgress(100);

      toast({
        title: "Success!",
        description: `Seeded ${data.customers} customers, ${data.suppliers} suppliers, ${data.sites} sites, ${data.accounts} accounts, and ${data.bills} bills (${data.total_records} total)`,
      });
    } catch (error: any) {
      console.error('Seed error:', error);
      toast({
        title: "Seeding failed",
        description: error.message || "Failed to seed data. Check console for details.",
        variant: "destructive"
      });
    } finally {
      setIsSeeding(false);
    }
  };

  const handleClearData = async () => {
    if (!confirm('Are you sure you want to clear all dummy data? This cannot be undone.')) {
      return;
    }

    setIsClearing(true);

    try {
      // Check session first
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        throw new Error('No active session. Please log in again.');
      }

      const { data, error } = await supabase.functions.invoke('seed-dummy-data', {
        body: { action: 'clear_dummy' }
      });

      if (error) throw error;

      setSeedResults(null);
      toast({
        title: "Data cleared",
        description: "All dummy data has been removed",
      });
    } catch (error: any) {
      console.error('Clear error:', error);
      toast({
        title: "Clear failed",
        description: error.message || "Failed to clear data. Check console for details.",
        variant: "destructive"
      });
    } finally {
      setIsClearing(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Developer Tools"
        description="Database seeding and testing utilities"
      />

      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          These tools are for development and testing purposes only. Use with caution in production environments.
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5" />
              Seed Dummy Data
            </CardTitle>
            <CardDescription>
              Populate the database with realistic test data for visualization
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">This will create:</p>
              <ul className="text-sm space-y-1 ml-4 list-disc text-muted-foreground">
                <li>15 Customers across various industries</li>
                <li>25 Utility Suppliers (PG&E, Edison, etc.)</li>
                <li>50 Sites (2-5 per customer)</li>
                <li>150 Accounts (1-4 per site)</li>
                <li>~1,800 Bills (12 months of data)</li>
              </ul>
            </div>

            {isSeeding && (
              <div className="space-y-2">
                <Progress value={progress} />
                <p className="text-sm text-center text-muted-foreground">Seeding data... {progress}%</p>
              </div>
            )}

            <Button 
              onClick={handleSeedData} 
              disabled={isSeeding || isClearing}
              className="w-full"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isSeeding ? 'animate-spin' : ''}`} />
              {isSeeding ? 'Seeding...' : 'Seed All Data'}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              Clear Dummy Data
            </CardTitle>
            <CardDescription>
              Remove all seeded test data from the database
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                This will permanently delete all dummy data. This action cannot be undone.
              </AlertDescription>
            </Alert>

            <Button 
              onClick={handleClearData} 
              disabled={isSeeding || isClearing}
              variant="destructive"
              className="w-full"
            >
              <Trash2 className={`h-4 w-4 mr-2`} />
              {isClearing ? 'Clearing...' : 'Clear All Dummy Data'}
            </Button>
          </CardContent>
        </Card>
      </div>

      {seedResults && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-success" />
              Seed Results
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="text-center p-4 rounded-lg bg-muted">
                <div className="text-2xl font-bold text-primary">{seedResults.customers || 0}</div>
                <p className="text-sm text-muted-foreground">Customers</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-muted">
                <div className="text-2xl font-bold text-accent">{seedResults.suppliers || 0}</div>
                <p className="text-sm text-muted-foreground">Suppliers</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-muted">
                <div className="text-2xl font-bold text-success">{seedResults.sites || 0}</div>
                <p className="text-sm text-muted-foreground">Sites</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-muted">
                <div className="text-2xl font-bold text-secondary">{seedResults.accounts || 0}</div>
                <p className="text-sm text-muted-foreground">Accounts</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-muted">
                <div className="text-2xl font-bold text-primary">{seedResults.bills || 0}</div>
                <p className="text-sm text-muted-foreground">Bills</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
